import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import { fetchServiceBookPersons } from "./serviceBookApi";

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const [chatMessages, setChatMessages] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState(["all"]);
  const [showInlineContext, setShowInlineContext] = useState(false);
  const [paused, setPaused] = useState(false);
  const [chatMode, setChatMode] = useState("normal");
  const [chatSessionId, setChatSessionId] = useState("");
  const [personsList, setPersonsList] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [deepHistoryId, setDeepHistoryId] = useState(null);
  const [plannerHistoryId, setPlannerHistoryId] = useState(null);
  const [useMemory, setUseMemory] = useState(false);

  const isServiceBookMode = selectedDepartments.includes("Service_Book");

  // Fetch persons when Service Book mode is activated
  useEffect(() => {
    if (isServiceBookMode) {
      fetchServiceBookPersons()
        .then(setPersonsList)
        .catch(() => setPersonsList([]));
    } else {
      setPersonsList([]);
      setSelectedPerson(null);
    }
  }, [isServiceBookMode]);

  useEffect(() => {
    // Initialize a per-session UUID to thread deep research chats
    try {
      const key = "chatSessionId";
      const existing = sessionStorage.getItem(key);
      if (existing) {
        setChatSessionId(existing);
        return;
      }
      const uuid = crypto?.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      sessionStorage.setItem(key, uuid);
      setChatSessionId(uuid);
    } catch {
      // Fallback if sessionStorage unavailable
      const uuid = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      setChatSessionId(uuid);
    }
  }, []);

  const value = useMemo(
    () => ({
      chatMessages,
      setChatMessages,
      selectedDepartments,
      setSelectedDepartments,
      showInlineContext,
      setShowInlineContext,
      paused,
      setPaused,
      chatMode,
      setChatMode,
      chatSessionId,
      conversationId,
      setConversationId,
      deepHistoryId,
      setDeepHistoryId,
      plannerHistoryId,
      setPlannerHistoryId,
      useMemory,
      setUseMemory,
      personsList,
      selectedPerson,
      setSelectedPerson,
      isServiceBookMode,
    }),
    [
      chatMessages,
      selectedDepartments,
      showInlineContext,
      paused,
      chatMode,
      chatSessionId,
      conversationId,
      deepHistoryId,
      plannerHistoryId,
      useMemory,
      personsList,
      selectedPerson,
      isServiceBookMode,
    ]
  );

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export function useChat() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChat must be used within a ChatProvider");
  return ctx;
}
